import {DataTableSortStatus} from 'mantine-datatable';
import {Application} from '../types';

// Column sorting for the applications catalog (FNP-913).
//
// Ported from flexi-testing-app `synthetic-users/utils/sortRecords.ts`, the reference
// implementation. ⚠️ When FNP-912 lands a SHARED sort utility, this file should
// be deleted and replaced with the shared import — it is a deliberate duplicate,
// not a fork, and every rule below is identical by design.
//
// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is NULLABLE on Application (`created?: string`, types.ts:71),
// which the module checklist flags as a risk: if EVERY row lacked it, the
// "sort" would silently become insertion order. It is used anyway, for two
// reasons that make it safe in practice:
//
//   1. The API populates it on write and orders by it — listApplications
//      queries record-type-created-index with scanIndexForward: false
//      (console-api applications/list.ts:79,83). So the default AGREES with the
//      server rather than reshuffling the first paint.
//   2. Nulls sort LAST (see `compare`), so any row missing it lands at the
//      bottom rather than corrupting the order of rows that have it.
//
// There is no non-null timestamp on this type to prefer instead. If one is ever
// added, switch to it.
//
// It is still applied client-side because this is an INFINITE QUERY: pages are
// concatenated as they arrive, and relying on arrival order would degrade
// silently if a page resolved out of order or a refetch merged pages.
//
// `created` is immutable, which is what makes it safe as a default: `updated`
// moves under an operator who is reading the list.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Application> = {
    columnAccessor: 'created',
    direction: 'desc'
};

// Every sortable field here is a string (name, url) an enum
// (applicationCategory, applicationStatus) or an ISO-8601 timestamp (created) —
// all of which order correctly with localeCompare. Numbers are handled anyway
// so a future numeric column does not silently compare as a string, which would
// put "10" before "9".
const compare = (a: unknown, b: unknown): number => {
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }
    // NULLS LAST, in both directions.
    //
    // A missing value is a STATE ("no URL set"), not a small value. Letting
    // nulls sort as '' would float every incomplete row to the top of an
    // ascending sort and make the column useless.
    const aMissing = a === null || a === undefined || a === '';
    const bMissing = b === null || b === undefined || b === '';
    if (aMissing || bMissing) {
        if (aMissing && bMissing) {
            return 0;
        }
        return aMissing ? 1 : -1;
    }
    return String(a).localeCompare(String(b));
};

/**
 * Sorts the records currently loaded in the browser.
 *
 * ⚠️ Scope: the pages fetched SO FAR, exactly like the keyword filter beside it.
 * With token pagination the client cannot order rows it has not seen, so this
 * sorts what is on screen rather than the whole catalog. That is a property of
 * the pagination model, not a bug to fix here.
 *
 * Nulls sort LAST regardless of direction — see `compare`.
 */
export const sortRecords = (
    records: Application[],
    sortStatus: DataTableSortStatus<Application>
): Application[] => {
    const accessor = sortStatus.columnAccessor as keyof Application;

    return [...records].sort((a, b) => {
        const result = compare(a[accessor], b[accessor]);
        // The null-last rule is deliberately NOT inverted with direction: a row
        // with no value is not "the smallest", it is absent, and it belongs at
        // the bottom either way.
        const aMissing = a[accessor] === null || a[accessor] === undefined || a[accessor] === '';
        const bMissing = b[accessor] === null || b[accessor] === undefined || b[accessor] === '';
        if (aMissing !== bMissing) {
            return result;
        }
        return sortStatus.direction === 'desc' ? -result : result;
    });
};
